// src/components/CreateEventForm.tsx
import React, { useState } from 'react';
import { Show, FlightInfo, HotelInfo } from '../types';

interface CreateEventFormProps {
  onCreate: (newShow: Show) => void;
}

const CreateEventForm: React.FC<CreateEventFormProps> = ({ onCreate }) => {
  const [newShow, setNewShow] = useState<Show>({
    id: Date.now(),
    date: new Date(),
    city: '',
    venue: '',
    time: new Date(),
    ticketInfo: '',
    ticketLink: '',
    image: '',
    description: '',
    flightInfo: { arrival: '', departure: '' },
    hotelInfo: { name: '', address: '', checkIn: '', checkOut: '', rooms: '', notes: '' },
    schedule: ''
  });

  // top‐level fields and schedule
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    // date & time need parsing to Date
    if (name === 'date') {
      setNewShow((prev) => ({ ...prev, date: new Date(value) }));
    } else if (name === 'time') {
      setNewShow((prev) => ({ ...prev, time: new Date(`${prev.date.toISOString().split('T')[0]}T${value}`) }));
    } else {
      setNewShow((prev) => ({ ...prev, [name]: value }));
    }
  };

  // nested flightInfo
  const handleFlightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewShow((prev) => ({
      ...prev,
      flightInfo: { ...(prev.flightInfo as FlightInfo), [name]: value },
    }));
  };

  // nested hotelInfo
  const handleHotelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewShow((prev) => ({
      ...prev,
      hotelInfo: { ...(prev.hotelInfo as HotelInfo), [name]: value },
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate({ ...newShow, id: Date.now() });
    // reset form
    setNewShow({
      id: Date.now(),
      date: new Date(),
      city: '',
      venue: '',
      time: new Date(),
      ticketInfo: '',
      ticketLink: '',
      image: '',
      description: '',
      flightInfo: { arrival: '', departure: '' },
      hotelInfo: { name: '', address: '', checkIn: '', checkOut: '', rooms: '', notes: '' },
      schedule: ''
    });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-800 p-6 rounded-lg space-y-4">
      <h2 className="text-2xl font-bold mb-4">Create New Event</h2>

      {/* Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-gray-300">City</label>
          <input
            type="text"
            name="city"
            value={newShow.city}
            onChange={handleChange}
            className="w-full p-2 bg-gray-700 text-gray-100 rounded"
            required
          />
        </div>
        <div>
          <label className="block text-gray-300">Venue</label>
          <input
            type="text"
            name="venue"
            value={newShow.venue}
            onChange={handleChange}
            className="w-full p-2 bg-gray-700 text-gray-100 rounded"
            required
          />
        </div>
        <div>
          <label className="block text-gray-300">Date</label>
          <input
            type="date"
            name="date"
            value={newShow.date.toISOString().split('T')[0]}
            onChange={handleChange}
            className="w-full p-2 bg-gray-700 text-gray-100 rounded"
            required
          />
        </div>
        <div>
          <label className="block text-gray-300">Time</label>
          <input
            type="time"
            name="time"
            value={newShow.time.toISOString().split('T')[1].slice(0, 5)}
            onChange={handleChange}
            className="w-full p-2 bg-gray-700 text-gray-100 rounded"
            required
          />
        </div>
      </div>

      {/* Ticket & Media */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-gray-300">Ticket Info</label>
          <input
            type="text"
            name="ticketInfo"
            value={newShow.ticketInfo}
            onChange={handleChange}
            className="w-full p-2 bg-gray-700 text-gray-100 rounded"
          />
        </div>
        
        <div className="md:col-span-2">
          <label className="block text-gray-300">Image URL</label>
          <input
            type="url"
            name="image"
            value={newShow.image}
            onChange={handleChange}
            className="w-full p-2 bg-gray-700 text-gray-100 rounded"
          />
        </div>
      </div>

      {/* Description & Schedule */}
      <div>
        <label className="block text-gray-300">Description</label>
        <textarea
          name="description"
          value={newShow.description}
          onChange={handleChange}
          className="w-full p-2 bg-gray-700 text-gray-100 rounded"
          rows={3}
        />
      </div>
      <div>
        <label className="block text-gray-300">Schedule (optional)</label>
        <input
          type="text"
          name="schedule"
          value={newShow.schedule}
          onChange={handleChange}
          className="w-full p-2 bg-gray-700 text-gray-100 rounded"
          placeholder="e.g. Doors 7pm, Show 8pm"
        />
      </div>

      {/* Flight Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-gray-300">Flight Arrival (optional)</label>
          <input
            type="text"
            name="arrival"
            value={newShow.flightInfo?.arrival}
            onChange={handleFlightChange}
            className="w-full p-2 bg-gray-700 text-gray-100 rounded"
            placeholder="e.g. June 1, 10:00 AM - JFK"
          />
        </div>
        <div>
          <label className="block text-gray-300">Flight Departure (optional)</label>
          <input
            type="text"
            name="departure"
            value={newShow.flightInfo?.departure}
            onChange={handleFlightChange}
            className="w-full p-2 bg-gray-700 text-gray-100 rounded"
            placeholder="e.g. June 2, 2:00 PM - LAX"
          />
        </div>
      </div>

      {/* Hotel Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-gray-300">Hotel Name (optional)</label>
          <input
            type="text"
            name="name"
            value={newShow.hotelInfo?.name}
            onChange={handleHotelChange}
            className="w-full p-2 bg-gray-700 text-gray-100 rounded"
          />
        </div>
        <div>
          <label className="block text-gray-300">Hotel Address (optional)</label>
          <input
            type="text"
            name="address"
            value={newShow.hotelInfo?.address}
            onChange={handleHotelChange}
            className="w-full p-2 bg-gray-700 text-gray-100 rounded"
          />
        </div>
        <div>
          <label className="block text-gray-300">Check-In Date (optional)</label>
          <input
            type="text"
            name="checkIn"
            value={newShow.hotelInfo?.checkIn}
            onChange={handleHotelChange}
            className="w-full p-2 bg-gray-700 text-gray-100 rounded"
            placeholder="e.g. June 1"
          />
        </div>
        <div>
          <label className="block text-gray-300">Check-Out Date (optional)</label>
          <input
            type="text"
            name="checkOut"
            value={newShow.hotelInfo?.checkOut}
            onChange={handleHotelChange}
            className="w-full p-2 bg-gray-700 text-gray-100 rounded"
            placeholder="e.g. June 2"
          />
        </div>
        <div>
          <label className="block text-gray-300">Rooms (optional)</label>
          <input
            type="text"
            name="rooms"
            value={newShow.hotelInfo?.rooms}
            onChange={handleHotelChange}
            className="w-full p-2 bg-gray-700 text-gray-100 rounded"
            placeholder="e.g. 2 rooms x 2 nights"
          />
        </div>
        <div>
          <label className="block text-gray-300">Hotel Notes (optional)</label>
          <input
            type="text"
            name="notes"
            value={newShow.hotelInfo?.notes}
            onChange={handleHotelChange}
            className="w-full p-2 bg-gray-700 text-gray-100 rounded"
            placeholder="e.g. Late check-in arranged"
          />
        </div>
      </div>

      <button
        type="submit"
        className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded mt-4"
      >
        Create Event
      </button>
    </form>
  );
};

export default CreateEventForm;
